import React from 'react';
import { cn } from '../../../lib/utils';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { QueryQuestionsParams, QuestionType } from '../../../api/questions';
import { Subject, Topic } from '../../../api/subjects';

interface QuestionsFiltersProps {
  isDark: boolean;
  filters: QueryQuestionsParams;
  setFilters: React.Dispatch<React.SetStateAction<QueryQuestionsParams>>;
  subjects: Subject[];
  topics: Topic[];
}

const ALL = '__all__';

export function QuestionsFilters({ isDark, filters, setFilters, subjects, topics }: QuestionsFiltersProps) {
  const labelCls = cn('text-xs', isDark ? 'text-slate-400' : 'text-slate-500');
  const triggerCls = cn(isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200');

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="space-y-1">
        <Label className={labelCls}>Subject</Label>
        <Select
          value={filters.subject_id || ALL}
          onValueChange={(v) =>
            setFilters((prev) => ({
              ...prev,
              subject_id: v === ALL ? undefined : v,
              topic_id: undefined,
              page: 1,
            }))
          }
        >
          <SelectTrigger className={triggerCls}>
            <SelectValue placeholder="All subjects" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All subjects</SelectItem>
            {subjects.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1">
        <Label className={labelCls}>Topic</Label>
        <Select
          value={filters.topic_id || ALL}
          disabled={!filters.subject_id}
          onValueChange={(v) =>
            setFilters((prev) => ({ ...prev, topic_id: v === ALL ? undefined : v, page: 1 }))
          }
        >
          <SelectTrigger className={triggerCls}>
            <SelectValue placeholder="All topics" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All topics</SelectItem>
            {topics.map((t) => (
              <SelectItem key={t.id} value={t.id}>
                {t.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1">
        <Label className={labelCls}>Type</Label>
        <Select
          value={filters.type || ALL}
          onValueChange={(v) =>
            setFilters((prev) => ({
              ...prev,
              type: v === ALL ? undefined : (v as QuestionType),
              page: 1,
            }))
          }
        >
          <SelectTrigger className={triggerCls}>
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All types</SelectItem>
            <SelectItem value="simple">Simple</SelectItem>
            <SelectItem value="complex_with_subs">Complex (sub-questions)</SelectItem>
            <SelectItem value="exclusive_subs">Exclusive subs</SelectItem>
            <SelectItem value="complex_with_input">Complex with input</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
